import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { CurrencyInr, DownloadSimple } from "@phosphor-icons/react";

export default function Payments() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/payments/report").then((r) => setData(r.data)).catch(() => setData({ suppliers: [], totals: {} }));
  }, []);

  const exportXlsx = async () => {
    try {
      const res = await api.get("/payments/export", { params: { format: "xlsx" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "viva_payments.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const t = data?.totals || {};

  return (
    <>
      <PageHeader title="Payments" subtitle="Supplier payables & payments — based on accepted (received) goods incl. GST">
        <Button variant="outline" onClick={exportXlsx} data-testid="payments-export" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider text-xs">
          <DownloadSimple size={16} weight="bold" className="mr-1" /> Export Excel
        </Button>
      </PageHeader>
      <div className="p-4 sm:p-8 space-y-6">
        {data === null ? (
          <p className="text-sm text-zinc-400">Loading…</p>
        ) : data.suppliers.length === 0 ? (
          <div className="bg-white border border-zinc-300 px-6 py-12 text-center" data-testid="payments-empty">
            <CurrencyInr size={40} weight="thin" className="text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No supplier billing activity yet.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-zinc-300 px-6 py-5"><p className="text-[11px] uppercase tracking-wider text-zinc-400">Total Payable</p><p className="font-mono text-2xl font-bold">₹{(t.payable ?? 0).toFixed(2)}</p></div>
              <div className="bg-white border border-zinc-300 px-6 py-5"><p className="text-[11px] uppercase tracking-wider text-zinc-400">Total Paid</p><p className="font-mono text-2xl font-bold text-[#16A34A]">₹{(t.paid ?? 0).toFixed(2)}</p></div>
              <div className="bg-white border border-zinc-300 px-6 py-5"><p className="text-[11px] uppercase tracking-wider text-zinc-400">Outstanding</p><p className="font-mono text-2xl font-bold text-[#DC2626]">₹{(t.pending ?? 0).toFixed(2)}</p></div>
            </div>
            <div className="bg-white border border-zinc-300" data-testid="payments-supplier-table">
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-2 px-6 py-3 border-b border-zinc-300 bg-zinc-50 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                <span>Supplier</span><span className="text-right">Payable</span><span className="text-right">Paid</span><span className="text-right">Outstanding</span>
              </div>
              {data.suppliers.map((s) => (
                <div key={s.supplier_name} className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-2 px-6 py-3 border-b border-zinc-100 last:border-0 text-sm" data-testid={`payment-supplier-${s.supplier_name}`}>
                  <span className="text-zinc-900">{s.supplier_name} <span className="text-zinc-400 text-xs">· {s.po_count} PO(s)</span></span>
                  <span className="text-right font-mono">₹{s.payable.toFixed(2)}</span>
                  <span className="text-right font-mono text-[#16A34A]">₹{s.paid.toFixed(2)}</span>
                  <span className={`text-right font-mono font-bold ${s.pending > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>₹{s.pending.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
